import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import {
  buscarPersonas,
  cambiarAsignacion,
  darDeBaja,
  fijarPlan,
  listarAsignaciones,
  listarTerritoriosEstructura,
  nombrar,
  verEstructura,
  type Asignacion,
  type NodoTerritorio,
  type PersonaDelPadron,
  type ResumenEstructura,
  type RolEstructura,
  type Yo,
} from "./api";
import { caminos } from "./arbol";
import { Cargando, Cifra, ErrorDeCarga, Insignia, Pantalla, Tabla, Vacio, type Columna } from "./componentes";
import { fecha, haceCuanto, numero, plural, porcentaje } from "./formato";
import { mensajeDeError } from "./mensajes";
import type { Navegar } from "./navegacion";

/**
 * ESTRUCTURA: quién responde por cada territorio (db/047).
 *
 * Una persona del padrón, varios roles con vigencia; nada se borra: la baja
 * es cerrar con motivo, el cambio es baja y alta en una. La pantalla
 * responde, en este orden, «¿cuántas secciones tienen responsable?»,
 * «¿cuántos de cada rol hay contra el plan?», «¿dónde falta?» (vacantes) y
 * «¿quién es?» (la lista). Nombrar es elegir a alguien del padrón —que vive
 * donde trabaja— y decir qué rol y en qué territorio.
 */

const TIPO: Record<string, string> = {
  estado: "Estado", municipio: "Municipio", distrito: "Distrito", zona: "Zona", seccion: "Sección", manzana: "Manzana",
};

type Vista = "vacantes" | "plan" | "lista";

/** Cuántos renglones se enseñan de golpe; el resto, a petición. */
const TRAMO = 40;

interface Alta {
  seccion: string;
  texto: string;
  candidatas: PersonaDelPadron[];
  persona: PersonaDelPadron | null;
  rol: string;
  territorio_id: number | null;
  nota: string;
}

function nombreDe(a: { nombre: string; primer_apellido: string; segundo_apellido: string | null }) {
  return [a.nombre, a.primer_apellido, a.segundo_apellido].filter(Boolean).join(" ");
}

export function Estructura({
  yo,
  parametros,
  navegar,
  avisar,
}: {
  yo: Yo;
  parametros: Record<string, unknown>;
  navegar: Navegar;
  avisar: (t: string) => void;
}) {
  const [resumen, setResumen] = useState<ResumenEstructura | null>(null);
  const [asignaciones, setAsignaciones] = useState<Asignacion[]>([]);
  const [nodos, setNodos] = useState<NodoTerritorio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filtroRol, setFiltroRol] = useState<string>(typeof parametros["rol"] === "string" ? (parametros["rol"] as string) : "");
  const [busqueda, setBusqueda] = useState("");
  const [conBajas, setConBajas] = useState(false);
  const [alta, setAlta] = useState<Alta | null>(null);
  const [plan, setPlan] = useState<Record<string, string> | null>(null);
  const [cambio, setCambio] = useState<{ a: Asignacion; territorio_id: string; motivo: string } | null>(null);
  const vistaInicial: Vista = parametros["ver"] === "plan" ? "plan" : "vacantes";
  const [vista] = useState<Vista>(vistaInicial);
  const [verVacantes, setVerVacantes] = useState(TRAMO);
  const [verFilas, setVerFilas] = useState(TRAMO);
  const puedeAdministrar = Boolean(yo.capacidades["administrar_estructura"]);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [r, a, t] = await Promise.all([
        verEstructura(),
        listarAsignaciones({ bajas: conBajas }),
        listarTerritoriosEstructura(),
      ]);
      setResumen(r);
      setAsignaciones(a.asignaciones);
      setNodos(t.nodos);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, [conBajas]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const nombreDelNodo = useMemo(() => caminos(nodos), [nodos]);
  const nodoDeSeccion = useMemo(() => {
    const m = new Map<number, NodoTerritorio>();
    for (const n of nodos) if (n.tipo === "seccion" && /^\d+$/.test(n.clave)) m.set(Number(n.clave), n);
    return m;
  }, [nodos]);
  const roles: RolEstructura[] = resumen?.roles ?? [];
  const rolDe = (clave: string) => roles.find((r) => r.clave === clave);

  // Cuando se llega desde «ver las vacantes» o con una sección en mano, la
  // alta arranca ahí.
  useEffect(() => {
    if (typeof parametros["seccion"] === "number" && puedeAdministrar && resumen && !alta) {
      empezarAlta(String(parametros["seccion"]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen]);

  function empezarAlta(seccion = "") {
    setAlta({ seccion, texto: "", candidatas: [], persona: null, rol: "seccional", territorio_id: null, nota: "" });
    if (seccion) void buscar({ seccion, texto: "" });
  }

  async function buscar(q: { seccion: string; texto: string }) {
    const seccion = q.seccion.trim() ? Number(q.seccion) : null;
    if (seccion !== null && !Number.isInteger(seccion)) {
      setError("La sección es un número.");
      return;
    }
    try {
      const r = await buscarPersonas({ ...(q.texto.trim() ? { q: q.texto.trim() } : {}), ...(seccion !== null ? { seccion } : {}) });
      setAlta((a) => (a ? { ...a, candidatas: r.personas, persona: null } : a));
      if (r.personas.length === 0) avisar("Nadie en el padrón con esos datos. Prueba otra sección o parte del nombre.");
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  function elegir(p: PersonaDelPadron) {
    setAlta((a) => {
      if (!a) return a;
      const rol = rolDe(a.rol);
      const propia = p.seccion != null ? nodoDeSeccion.get(p.seccion) : undefined;
      const territorio = rol?.ambito_tipo.includes("seccion") && propia ? propia.id : a.territorio_id;
      return { ...a, persona: p, territorio_id: territorio };
    });
  }

  function cambiarRol(clave: string) {
    setAlta((a) => {
      if (!a) return a;
      const rol = rolDe(clave);
      const propia = a.persona?.seccion != null ? nodoDeSeccion.get(a.persona.seccion) : undefined;
      const territorio = rol?.ambito_tipo.includes("seccion") && propia ? propia.id : null;
      return { ...a, rol: clave, territorio_id: territorio };
    });
  }

  async function guardarAlta(ev: FormEvent) {
    ev.preventDefault();
    if (!alta?.persona || !alta.territorio_id) return;
    try {
      await nombrar({ persona_id: alta.persona.id, rol: alta.rol, territorio_id: alta.territorio_id, nota: alta.nota.trim() || null });
      avisar(`${nombreDe(alta.persona)} queda como ${rolDe(alta.rol)?.nombre.toLowerCase() ?? alta.rol} en ${nombreDelNodo.get(alta.territorio_id) ?? "ese territorio"}.`);
      setAlta(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function baja(a: Asignacion) {
    const motivo = window.prompt(`¿Por qué se da de baja a ${nombreDe(a)} como ${a.rol_nombre.toLowerCase()}?`);
    if (!motivo || motivo.trim().length < 5) return;
    try {
      await darDeBaja(a.id, motivo.trim());
      avisar(`${nombreDe(a)} dado de baja; queda en el historial.`);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function guardarCambio(ev: FormEvent) {
    ev.preventDefault();
    if (!cambio) return;
    const destino = Number(cambio.territorio_id);
    if (!Number.isInteger(destino) || destino < 1) {
      setError("Elige el territorio al que se mueve.");
      return;
    }
    try {
      await cambiarAsignacion(cambio.a.id, destino, cambio.motivo.trim());
      avisar(`${nombreDe(cambio.a)} ahora responde por ${nombreDelNodo.get(destino) ?? "el territorio nuevo"}.`);
      setCambio(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function guardarPlan(ev: FormEvent) {
    ev.preventDefault();
    if (!plan) return;
    const posiciones: Record<string, number> = {};
    for (const [k, v] of Object.entries(plan)) {
      if (v.trim() === "") continue;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0) {
        setError("Las posiciones son enteros, cero o más.");
        return;
      }
      posiciones[k] = n;
    }
    try {
      await fijarPlan(posiciones);
      avisar("Plan por rol guardado. Las tareas de «faltan por nombrar» salen de aquí.");
      setPlan(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (error && !resumen) {
    return (
      <Pantalla titulo="Estructura">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }
  if (!resumen) {
    return (
      <Pantalla titulo="Estructura">
        <Cargando que="la estructura" />
      </Pantalla>
    );
  }

  const q = busqueda.trim().toLowerCase();
  const filas = asignaciones.filter(
    (a) =>
      (!filtroRol || a.rol === filtroRol) &&
      (!q || nombreDe(a).toLowerCase().includes(q) || String(a.seccion ?? "").includes(q) || (a.territorio_nombre ?? "").toLowerCase().includes(q)),
  );
  const rolAlta = alta ? rolDe(alta.rol) : undefined;
  const territoriosParaRol = (r: RolEstructura | undefined) => nodos.filter((n) => r?.ambito_tipo.includes(n.tipo));

  const columnas: Columna<Asignacion>[] = [
    {
      id: "persona",
      titulo: "Persona",
      clave: (a) => `${a.primer_apellido} ${a.nombre}`,
      celda: (a) => (
        <>
          <strong>{nombreDe(a)}</strong>
          {a.telefono ? <span className="apagado chico"> · {a.telefono}</span> : null}
          {a.hasta ? (
            <>
              {" "}
              <Insignia tono="neutro">baja {fecha(a.hasta)}</Insignia>
            </>
          ) : null}
        </>
      ),
    },
    { id: "rol", titulo: "Rol", clave: (a) => a.rol_orden, celda: (a) => a.rol_nombre },
    {
      id: "territorio",
      titulo: "Territorio",
      clave: (a) => a.territorio_clave,
      celda: (a) => (
        <>
          <span className="apagado chico">{TIPO[a.territorio_tipo] ?? a.territorio_tipo}</span>{" "}
          <strong className={a.seccion ? "usuario" : undefined}>{a.seccion ?? a.territorio_nombre ?? a.territorio_clave}</strong>
        </>
      ),
    },
    { id: "desde", titulo: "Desde", clave: (a) => a.desde, celda: (a) => <span title={fecha(a.desde)}>{haceCuanto(a.desde)}</span> },
    {
      id: "nota",
      titulo: "Nota",
      celda: (a) => <span className="apagado chico">{a.hasta ? a.motivo_baja : a.nota ?? ""}</span>,
    },
    {
      id: "acciones",
      titulo: "Acciones",
      sinTitulo: true,
      celda: (a) => (
        <div className="acciones">
          {a.seccion && (
            <button type="button" className="btn terciario fila" title={`Ver la sección ${a.seccion} en el mapa`} onClick={() => navegar({ pantalla: "mapa", parametros: { seccion: a.seccion, capa: "responsable" } })}>
              Mapa
            </button>
          )}
          {puedeAdministrar && !a.hasta && (
            <>
              <button type="button" className="btn terciario fila" onClick={() => setCambio({ a, territorio_id: "", motivo: "" })}>
                Cambiar
              </button>
              <button type="button" className="btn terciario fila" onClick={() => void baja(a)}>
                Baja
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const columnasVacantes: Columna<ResumenEstructura["vacantes_lista"][number]>[] = [
    { id: "seccion", titulo: "Sección", clave: (v) => v.seccion, celda: (v) => <strong className="usuario">{v.seccion}</strong> },
    { id: "personas", titulo: "Personas en el padrón", numero: true, clave: (v) => v.personas, celda: (v) => numero(v.personas) },
    {
      id: "otros",
      titulo: "Otros roles ahí",
      clave: (v) => v.manzaneras + v.rc + v.rg,
      celda: (v) =>
        v.manzaneras + v.rc + v.rg === 0 ? (
          <span className="apagado">nadie</span>
        ) : (
          [v.manzaneras ? plural(v.manzaneras, "manzanera", "manzaneras") : "", v.rc ? `${v.rc} RC` : "", v.rg ? `${v.rg} RG` : ""].filter(Boolean).join(" · ")
        ),
    },
    {
      id: "acciones",
      titulo: "Acciones",
      sinTitulo: true,
      celda: (v) => (
        <div className="acciones">
          {puedeAdministrar && (
            <button type="button" className="btn secundario fila" onClick={() => empezarAlta(String(v.seccion))}>
              Nombrar aquí
            </button>
          )}
          <button type="button" className="btn terciario fila" onClick={() => navegar({ pantalla: "mapa", parametros: { seccion: v.seccion, capa: "responsable" } })}>
            Mapa
          </button>
        </div>
      ),
    },
  ];

  return (
    <Pantalla
      titulo="Estructura"
      descripcion="Quién responde por cada territorio: seccionales, manzaneras, representantes de casilla y generales, contra lo que la campaña planea."
      acciones={
        puedeAdministrar && !alta ? (
          <button type="button" className="btn primario" onClick={() => empezarAlta()}>
            Nombrar
          </button>
        ) : undefined
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* ── El alta ───────────────────────────────────────────── */}
      {alta && (
        <form className="tarjeta formulario" onSubmit={(ev) => void guardarAlta(ev)} aria-labelledby="e-alta">
          <h2 id="e-alta">Nombrar a alguien del padrón</h2>
          <p className="ayuda">Primero quién (por su sección o por su nombre), luego qué rol y dónde. El seccional vive donde trabaja: su sección se propone sola.</p>
          <div className="campos-en-fila">
            <div className="campo">
              <label htmlFor="e-seccion">Sección</label>
              <input id="e-seccion" inputMode="numeric" value={alta.seccion} onChange={(e) => setAlta({ ...alta, seccion: e.target.value })} />
            </div>
            <div className="campo">
              <label htmlFor="e-texto">Nombre o teléfono</label>
              <input id="e-texto" value={alta.texto} onChange={(e) => setAlta({ ...alta, texto: e.target.value })} />
            </div>
            <div className="campo">
              <span aria-hidden="true">&nbsp;</span>
              <button type="button" className="btn secundario" onClick={() => void buscar(alta)} disabled={!alta.seccion.trim() && !alta.texto.trim()}>
                Buscar en el padrón
              </button>
            </div>
          </div>
          {alta.candidatas.length > 0 && (
            <fieldset className="candidatas">
              <legend>¿Quién?</legend>
              {alta.candidatas.map((p) => (
                <label key={p.id} className={alta.persona?.id === p.id ? "activo" : undefined}>
                  <input type="radio" name="persona" checked={alta.persona?.id === p.id} onChange={() => elegir(p)} />
                  <strong>{nombreDe(p)}</strong>
                  <span className="apagado chico">
                    {" "}
                    · sección {p.seccion ?? "sin resolver"}
                    {p.telefono ? ` · ${p.telefono}` : ""}
                    {p.roles ? ` · ya es ${p.roles}` : ""}
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          {alta.persona && (
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="e-rol">Rol</label>
                <select id="e-rol" value={alta.rol} onChange={(e) => cambiarRol(e.target.value)}>
                  {roles.map((r) => (
                    <option key={r.clave} value={r.clave}>
                      {r.nombre}
                    </option>
                  ))}
                </select>
                <p className="ayuda">Se asigna a: {(rolAlta?.ambito_tipo ?? []).map((t) => TIPO[t]?.toLowerCase() ?? t).join(" o ")}.</p>
              </div>
              <div className="campo">
                <label htmlFor="e-territorio">Territorio</label>
                <select id="e-territorio" value={alta.territorio_id ?? ""} onChange={(e) => setAlta({ ...alta, territorio_id: e.target.value === "" ? null : Number(e.target.value) })}>
                  <option value="">Elige…</option>
                  {territoriosParaRol(rolAlta).map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.tipo === "seccion" ? `Sección ${n.clave}` : nombreDelNodo.get(n.id)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label htmlFor="e-nota">Nota</label>
                <input id="e-nota" value={alta.nota} placeholder="opcional: quién la propuso, desde cuándo…" onChange={(e) => setAlta({ ...alta, nota: e.target.value })} />
              </div>
            </div>
          )}
          <div className="acciones">
            <button type="submit" className="btn primario" disabled={!alta.persona || !alta.territorio_id}>
              Nombrar
            </button>
            <button type="button" className="btn terciario" onClick={() => setAlta(null)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ── El cambio ─────────────────────────────────────────── */}
      {cambio && (
        <form className="tarjeta formulario" onSubmit={(ev) => void guardarCambio(ev)} aria-labelledby="e-cambio">
          <h2 id="e-cambio">
            Mover a {nombreDe(cambio.a)} ({cambio.a.rol_nombre.toLowerCase()})
          </h2>
          <p className="ayuda">Se cierra lo que tiene y se abre lo nuevo en la misma operación; el historial guarda las dos.</p>
          <div className="campos-en-fila">
            <div className="campo">
              <label htmlFor="c-territorio">A qué territorio</label>
              <select id="c-territorio" value={cambio.territorio_id} onChange={(e) => setCambio({ ...cambio, territorio_id: e.target.value })}>
                <option value="">Elige…</option>
                {territoriosParaRol(rolDe(cambio.a.rol))
                  .filter((n) => n.id !== cambio.a.territorio_id)
                  .map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.tipo === "seccion" ? `Sección ${n.clave}` : nombreDelNodo.get(n.id)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="c-motivo">Motivo</label>
              <input id="c-motivo" value={cambio.motivo} onChange={(e) => setCambio({ ...cambio, motivo: e.target.value })} />
            </div>
          </div>
          <div className="acciones">
            <button type="submit" className="btn primario" disabled={cambio.motivo.trim().length < 5 || !cambio.territorio_id}>
              Mover
            </button>
            <button type="button" className="btn terciario" onClick={() => setCambio(null)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ── El resumen ────────────────────────────────────────── */}
      <section aria-labelledby="e-resumen">
        <div className="encabezado">
          <h2 id="e-resumen">Contra el plan</h2>
          {puedeAdministrar && !plan && (
            <button type="button" className="btn secundario" onClick={() => setPlan(Object.fromEntries(roles.map((r) => [r.clave, r.plan == null ? "" : String(r.plan)])))}>
              {roles.some((r) => r.plan != null) ? "Cambiar el plan" : "Fijar el plan"}
            </button>
          )}
        </div>
        {resumen.plan_es_de_toda_la_campana && roles.some((r) => r.plan != null) && (
          <p className="ayuda">El plan es de toda la campaña; tus vigentes son solo los de tu ámbito, así que «faltan» se lee en el tablero de quien ve todo.</p>
        )}
        <div className="cifras">
          <Cifra
            etiqueta="secciones con responsable"
            valor={`${numero(resumen.con_responsable)} de ${numero(resumen.secciones)}`}
            detalle={resumen.secciones ? `${porcentaje(resumen.con_responsable, resumen.secciones)} · ${plural(resumen.vacantes, "vacante", "vacantes")}` : "sin secciones"}
            tono={resumen.secciones === 0 ? "neutro" : resumen.vacantes === 0 ? "exito" : resumen.vacantes * 2 > resumen.secciones ? "peligro" : "aviso"}
            alAbrir={() => navegar({ pantalla: "mapa", parametros: { capa: "responsable" } })}
          />
          {roles.map((r) => (
            <Cifra
              key={r.clave}
              etiqueta={r.plural.toLowerCase()}
              valor={r.plan != null && r.faltan != null ? `${numero(r.vigentes)} / ${numero(r.plan)}` : numero(r.vigentes)}
              detalle={r.plan == null ? "sin plan" : r.faltan == null ? `plan de la campaña: ${numero(r.plan)}` : r.faltan ? `faltan ${numero(r.faltan)}` : "plan cubierto"}
              tono={r.plan == null || r.faltan == null ? "neutro" : r.faltan ? (r.faltan * 2 > r.plan ? "peligro" : "aviso") : "exito"}
              alAbrir={() => setFiltroRol(r.clave)}
            />
          ))}
        </div>
        {plan && (
          <form className="tarjeta formulario" onSubmit={(ev) => void guardarPlan(ev)} aria-labelledby="e-plan">
            <h3 id="e-plan">Cuántas posiciones planea la campaña por rol</h3>
            <div className="campos-en-fila">
              {roles.map((r) => (
                <div className="campo" key={r.clave}>
                  <label htmlFor={`p-${r.clave}`}>{r.plural}</label>
                  <input id={`p-${r.clave}`} inputMode="numeric" value={plan[r.clave] ?? ""} placeholder="sin plan" onChange={(e) => setPlan({ ...plan, [r.clave]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="acciones">
              <button type="submit" className="btn primario">
                Guardar el plan
              </button>
              <button type="button" className="btn terciario" onClick={() => setPlan(null)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </section>

      {/* ── Las vacantes ──────────────────────────────────────── */}
      <section aria-labelledby="e-vacantes" id="vacantes">
        <h2 id="e-vacantes">Secciones sin responsable</h2>
        {resumen.vacantes === 0 ? (
          <Vacio titulo="Todas tus secciones tienen responsable" detalle="Cada sección del ámbito tiene al menos un seccional vigente." />
        ) : (
          <>
            <Tabla
              columnas={columnasVacantes}
              filas={resumen.vacantes_lista.slice(0, verVacantes)}
              claveDe={(v) => v.seccion}
              caption="Las secciones de tu ámbito sin seccional vigente; primero las que más padrón tienen y nadie cuida."
              ordenInicial={{ columna: "personas", desc: true }}
              vacio="Sin vacantes."
            />
            {resumen.vacantes_lista.length > verVacantes && (
              <p className="mas">
                <button type="button" className="btn secundario" onClick={() => setVerVacantes(verVacantes + TRAMO)}>
                  Mostrar {Math.min(TRAMO, resumen.vacantes_lista.length - verVacantes)} más
                </button>{" "}
                <span className="apagado chico">{numero(verVacantes)} de {numero(resumen.vacantes_lista.length)}</span>
              </p>
            )}
          </>
        )}
      </section>

      {/* ── La lista ──────────────────────────────────────────── */}
      <section aria-labelledby="e-lista">
        <h2 id="e-lista">Nombramientos</h2>
        <div className="herramientas">
          <fieldset className="segmentos" aria-label="Rol">
            <legend className="solo-lectores">Rol</legend>
            {([["", "Todos"], ...roles.map((r) => [r.clave, r.plural])] as [string, string][]).map(([f, t]) => (
              <label key={f} className={filtroRol === f ? "activo" : undefined}>
                <input type="radio" name="rol" value={f} checked={filtroRol === f} onChange={() => setFiltroRol(f)} />
                {t}
              </label>
            ))}
          </fieldset>
          <label className="solo-lectores" htmlFor="e-buscar">
            Buscar por nombre, sección o territorio
          </label>
          <input id="e-buscar" className="buscar" type="search" placeholder="Buscar por nombre o sección…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          <label className="cuenta">
            <input type="checkbox" checked={conBajas} onChange={(e) => setConBajas(e.target.checked)} /> incluir bajas
          </label>
          <span className="cuenta">{plural(filas.length, "nombramiento", "nombramientos")}</span>
        </div>
        <Tabla
          columnas={columnas}
          filas={filas.slice(0, verFilas)}
          claveDe={(a) => a.id}
          caption="Quién responde por qué, desde cuándo. Nada se borra: una baja queda con su motivo."
          ordenInicial={{ columna: "rol", desc: false }}
          vacio={vista === "lista" ? "Nadie nombrado todavía." : "Nadie con ese rol en tu ámbito."}
        />
        {filas.length > verFilas && (
          <p className="mas">
            <button type="button" className="btn secundario" onClick={() => setVerFilas(verFilas + TRAMO)}>
              Mostrar {Math.min(TRAMO, filas.length - verFilas)} más
            </button>{" "}
            <span className="apagado chico">{numero(verFilas)} de {numero(filas.length)}</span>
          </p>
        )}
      </section>
    </Pantalla>
  );
}
